import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PhoneInputWithCode } from "@/components/PhoneInputWithCode";
import axios from "axios";
import { Eye, Check, Loader2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ObservationPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [data, setData] = useState(null);

  const [clinicianInfo, setClinicianInfo] = useState({
    title: "",
    license_number: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_country_code: "+1|US",
    additional_phone: "",
    additional_phone_country_code: "+1|US",
  });
  const [hoursConfirmed, setHoursConfirmed] = useState("");
  const [datesOfCompletion, setDatesOfCompletion] = useState("");
  const [observationType, setObservationType] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/observation/${token}`);
        if (res.data.completed) {
          setCompleted(true);
          setSuccessMsg(res.data.message);
        } else {
          setData(res.data);
          const obs = res.data.observation;
          setClinicianInfo(prev => ({
            ...prev,
            first_name: obs.supervisor_first_name,
            last_name: obs.supervisor_last_name,
            email: obs.supervisor_email,
            phone: obs.supervisor_phone || "",
          }));
          setObservationType(obs.observation_type || "");
          setDatesOfCompletion(obs.dates_completed || "");
        }
      } catch (err) {
        setError(err.response?.data?.detail || "This link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSubmit = async () => {
    if (!hoursConfirmed) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/observation/${token}/submit`, {
        clinician_info: clinicianInfo,
        hours_confirmed: parseInt(hoursConfirmed),
        dates_of_completion: datesOfCompletion,
        observation_type: observationType,
        description,
      });
      setCompleted(true);
      setSuccessMsg(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A1D8E0]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-red-500/20 bg-red-500/5 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Link Invalid</h2>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-[#739600]/30 bg-[#739600]/5 p-8 text-center">
          <Check className="w-12 h-12 text-[#739600] mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Thank You!</h2>
          <p className="text-slate-400 text-sm">{successMsg}</p>
        </div>
      </div>
    );
  }

  const obs = data.observation;
  const inputCls = "h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600";
  const labelCls = "text-sm text-slate-300 mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#0a0e14] py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00677F]/20 via-transparent to-[#7B68EE]/10" />
          <div className="relative p-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00677F]/20 border border-[#00677F]/30 mb-4">
              <Eye className="w-3.5 h-3.5 text-[#A1D8E0]" />
              <span className="text-[#A1D8E0] text-xs font-medium tracking-wide uppercase">University of St. Augustine</span>
            </div>
            <h1 className="text-white text-2xl font-semibold mb-3">Verification of Observation Hours</h1>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xl mx-auto">
              <span className="font-medium text-white">{data.applicant_name}</span> has listed you as a supervising clinician for observation hours completed as part of their application to the <span className="font-medium text-[#A1D8E0]">{data.program}</span> program. Please verify the information below.
            </p>
          </div>
        </div>

        {/* Applicant Info */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <h3 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider">Applicant Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Applicant Name</span>
              <p className="text-white">{data.applicant_name}</p>
            </div>
            <div>
              <span className="text-slate-500">Program</span>
              <p className="text-white">{data.program}</p>
            </div>
          </div>
        </div>

        {/* Supervising Clinician Information */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-5">
          <h3 className="text-white text-sm font-semibold uppercase tracking-wider">Supervising Clinician Information</h3>

          <div>
            <Label className={labelCls}>Facility Name / City / State</Label>
            <p className="text-white text-sm">{obs.facility_name}{obs.city ? `, ${obs.city}` : ""}{obs.state ? `, ${obs.state}` : ""}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Title</Label>
              <Input value={clinicianInfo.title} onChange={(e) => setClinicianInfo({ ...clinicianInfo, title: e.target.value })} placeholder="OTR, PT, etc." className={inputCls} data-testid="portal-title" />
            </div>
            <div>
              <Label className={labelCls}>License Number</Label>
              <Input value={clinicianInfo.license_number} onChange={(e) => setClinicianInfo({ ...clinicianInfo, license_number: e.target.value })} placeholder="License #" className={inputCls} data-testid="portal-license" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>First Name</Label>
              <Input value={clinicianInfo.first_name} onChange={(e) => setClinicianInfo({ ...clinicianInfo, first_name: e.target.value })} className={inputCls} data-testid="portal-first-name" />
            </div>
            <div>
              <Label className={labelCls}>Last Name</Label>
              <Input value={clinicianInfo.last_name} onChange={(e) => setClinicianInfo({ ...clinicianInfo, last_name: e.target.value })} className={inputCls} data-testid="portal-last-name" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Email</Label>
              <Input value={clinicianInfo.email} onChange={(e) => setClinicianInfo({ ...clinicianInfo, email: e.target.value })} className={inputCls} data-testid="portal-email" />
            </div>
            <div>
              <Label className={labelCls}>Phone</Label>
              <PhoneInputWithCode
                value={clinicianInfo.phone}
                countryCode={clinicianInfo.phone_country_code}
                onPhoneChange={(val) => setClinicianInfo({ ...clinicianInfo, phone: val })}
                onCodeChange={(val) => setClinicianInfo({ ...clinicianInfo, phone_country_code: val })}
                testId="portal-phone"
                theme="dark"
              />
            </div>
          </div>

          <div>
            <Label className={labelCls}>Additional Phone</Label>
            <div className="max-w-xs">
              <PhoneInputWithCode
                value={clinicianInfo.additional_phone}
                countryCode={clinicianInfo.additional_phone_country_code}
                onPhoneChange={(val) => setClinicianInfo({ ...clinicianInfo, additional_phone: val })}
                onCodeChange={(val) => setClinicianInfo({ ...clinicianInfo, additional_phone_country_code: val })}
                testId="portal-addl-phone"
                theme="dark"
              />
            </div>
          </div>
        </div>

        {/* Details and Confirmation */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-5">
          <h3 className="text-white text-sm font-semibold uppercase tracking-wider">Details and Confirmation</h3>

          <div>
            <Label className={labelCls}>Total Hours to be Verified</Label>
            <p className="text-slate-400 text-xs mb-2">The applicant reported <span className="text-white font-medium">{obs.hours_requested} hours</span></p>
            <Select value={hoursConfirmed} onValueChange={setHoursConfirmed}>
              <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl max-w-xs`} data-testid="portal-hours">
                <SelectValue placeholder="Confirm hours" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1419] border-white/10 max-h-60">
                {[...Array(Math.max(obs.hours_requested + 10, 50))].map((_, i) => (
                  <SelectItem key={i} value={String(i)} className="text-slate-300">{i} hours</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className={labelCls}>Date(s) of Completion</Label>
            <Textarea
              value={datesOfCompletion}
              onChange={(e) => setDatesOfCompletion(e.target.value)}
              placeholder="e.g., June 2024 - August 2024"
              className="bg-black/30 border-white/[0.08] text-white rounded-xl px-4 py-3 focus:border-[#A1D8E0] placeholder:text-slate-600 min-h-[80px]"
              data-testid="portal-dates"
            />
          </div>

          <div>
            <Label className={labelCls}>Type of Observation</Label>
            <RadioGroup value={observationType} onValueChange={setObservationType} className="flex gap-4">
              {["Occupational Therapy", "Physical Therapy", "Other"].map((t) => (
                <div key={t} className={`flex items-center space-x-2 px-5 py-3 rounded-full border cursor-pointer transition-all ${
                  observationType === t ? "border-[#00677F]/50 bg-[#00677F]/10" : "border-white/[0.08] bg-black/30 hover:border-white/20"
                }`}>
                  <RadioGroupItem value={t} id={`obs-type-${t}`} />
                  <Label htmlFor={`obs-type-${t}`} className="text-white cursor-pointer text-sm">{t}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label className={labelCls}>Describe what the applicant observed</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of observation activities..."
              className="bg-black/30 border-white/[0.08] text-white rounded-xl px-4 py-3 focus:border-[#A1D8E0] placeholder:text-slate-600 min-h-[100px]"
              data-testid="portal-description"
            />
          </div>

          <div className="p-4 rounded-xl bg-[#7B68EE]/5 border border-[#7B68EE]/20 text-slate-400 text-xs leading-relaxed">
            <strong className="text-[#7B68EE]">FERPA Notice:</strong> The information contained in this form is confidential. It is maintained for the sole purpose of evaluating this applicant for admission to the University of St. Augustine for Health Sciences.
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 pb-8">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
            onClick={() => window.close()}
            data-testid="portal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !hoursConfirmed}
            className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(0,103,127,0.3)]"
            data-testid="portal-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}
